import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store";

import CustomNavbar from "../../components/common/CustomNavbar";
import PageLayout from "../../components/layouts/PageLayout";

import { IoArrowBack } from "react-icons/io5";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import {
    fetchCompanyById,
    createToken,
    fetchToken,
} from "../../features/developer/DeveoperServices";
import { CompanyDetails, TokenData } from "../../types/developerTypes";
import ECSLoader from "../../components/common/EcsLoader";

export default function CompanyDetail() {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const { id } = useParams<{ id: string }>();

    const [loading, setLoading] = useState(true);
    const [company, setCompany] = useState<CompanyDetails | null>(null);
    const [tokens, setTokens] = useState<TokenData[]>([]);
    const [generating, setGenerating] = useState(false);

    const activeToken = tokens.find((t) => t.active) || tokens[0] || null;

    useEffect(() => {
        if (!id) return;

        // Fetch company by ID + its tokens
        dispatch(fetchCompanyById(Number(id)))
            .unwrap()
            .then(async (companyData: any) => {
                setCompany(companyData);

                try {
                    const tokenRes = await dispatch(fetchToken(companyData.id)).unwrap();
                    setTokens(tokenRes);
                } catch {
                    setTokens([]);
                }
            })
            .catch(() => { })
            .finally(() => setLoading(false));
    }, [dispatch, id]);

    const handleGenerateToken = async () => {
        if (!company?.id) return;

        setGenerating(true);
        try {
            await dispatch(createToken(company.id)).unwrap();

            const updatedTokens = await dispatch(fetchToken(company.id)).unwrap();
            setTokens(updatedTokens);

            toast.success("Token generated!");
        } catch (err: any) {
            toast.error(err?.message || "Failed to generate token");
        } finally {
            setGenerating(false);
        }
    };

    const handleCopy = () => {
        if (!activeToken?.token) return;
        navigator.clipboard.writeText(activeToken.token);
        toast.success("Token copied!");
    };

    return (
        <PageLayout
            customNavbar={
                <CustomNavbar>
                    <div className="flex gap-3 items-center w-max">
                        <IoArrowBack
                            className="text-2xl cursor-pointer hover:text-brand-main"
                            onClick={() => navigate("/admin/developer/company")}
                        />
                        <span className="font-bold text-lg uppercase">Company Details</span>
                    </div>
                </CustomNavbar>
            }
        >
            <div className="bg-white rounded-4xl p-7 flex flex-col gap-8">
                {loading ? (
                    <ECSLoader />
                ) : !company ? (
                    <div className="text-center py-16 text-gray-400">
                        <p>Company not found.</p>
                    </div>
                ) : (
                    <div className="flex flex-col gap-5">
                        {/* Header */}
                        <div className="flex justify-between items-center">
                            <h2 className="text-2xl font-bold text-gray-800">
                                Your Registered Company
                            </h2>

                            {company.active && (
                                <button
                                    onClick={handleGenerateToken}
                                    disabled={generating}
                                    className="flex items-center justify-center px-5 py-3 rounded-4xl bg-brand-blue-gradient text-white font-bold whitespace-nowrap"
                                >
                                    {generating
                                        ? "Generating..."
                                        : activeToken
                                            ? "Regenerate Token"
                                            : "Register Token"}
                                </button>
                            )}
                        </div>

                        {/* Company Card */}
                        <div className="bg-green-50 border border-green-100 p-5 rounded-xl">
                            <p className="text-lg font-semibold text-gray-800">
                                {company.name}
                            </p>

                            <a
                                href={
                                    company.website.startsWith("http")
                                        ? company.website
                                        : `https://${company.website}`
                                }
                                target="_blank"
                                rel="noreferrer"
                                className="text-blue-600 underline break-all"
                            >
                                {company.website}
                            </a>

                            {company.active ? (
                                <p className="mt-2 text-sm font-medium text-green-600">
                                    Active
                                </p>
                            ) : (
                                <div className="mt-3 text-sm text-yellow-700 bg-yellow-50 border border-yellow-100 p-3 rounded-lg">
                                    Your company is under review. This usually takes up to 12
                                    hours.
                                </div>
                            )}
                        </div>

                        {/* Token Section */}
                        {activeToken && (
                            <div className="bg-gray-50 border p-4 rounded-xl flex flex-col gap-3">
                                <p className="text-sm text-gray-600 font-medium">
                                    Your API Token
                                </p>

                                <div className="flex items-center justify-between bg-white border rounded-lg px-3 py-2">
                                    <span className="text-xs break-all text-gray-700">
                                        {activeToken.token}
                                    </span>

                                    <button
                                        onClick={handleCopy}
                                        className="ml-3 text-sm text-blue-600 font-semibold whitespace-nowrap"
                                    >
                                        Copy
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </PageLayout>
    );
}